// Canvas pointer-event state machine for FlowchartBuilder, extracted out of the
// component body: box drag, canvas pan, zoom, area draw/drag/resize continuation,
// drag-to-create, marquee selection, and the whole touch/pinch/wheel gesture layer.
// Pure re-wiring — every branch, comment, and dependency array below is moved
// verbatim from FlowchartBuilder.tsx, nothing about the logic itself changed.
//
// Scope note: this hook owns handleDown/handleMove/handleUp and the window
// touch/wheel effect (matching CLAUDE.md's plan for this extraction), but NOT the
// canvas-level onMouseDown/onContextMenu JSX handlers that START a marquee-select or
// an area-draw gesture — those stay inline in FlowchartBuilder.tsx because they reach
// into several area/UI-state pieces (isDrawingAreaMode, editingAreaId, contextMenu,
// linkPanel, showHistory, connecting, ...) that belong to other features, not the
// pointer state machine itself. This hook only *continues* and *finishes* gestures
// that are already in progress (isPanningRef/dragging/selectionRect/drawingArea/
// draggingArea/resizingArea/draftBox all being non-null/truthy is what every branch
// below keys off), which is a clean, well-defined boundary. Panning is the one
// exception with its own exported start function (startPanning) rather than being
// started inline in FlowchartBuilder.tsx like the others — see isPanningRef's own
// comment for why.
import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, RefObject, SetStateAction } from 'react';
import { computeSnap } from './lib/snapping';
import type { Area, Box, Arrow, ClickConnectMode, DraftBox, Point, SelectionRectState, SnapLine } from './types/index';

const SNAP_THRESHOLD = 8; // pixels - distance to trigger snapping

type IsPanningState = false | { startX: number; startY: number; initialPanX: number; initialPanY: number };
type DrawingAreaState = { startX: number; startY: number; currentX: number; currentY: number } | null;
type DraggingAreaState = { id: number; startMouseX: number; startMouseY: number; startAreaX: number; startAreaY: number } | null;
type ResizingAreaState = { id: number; handle: 'nw' | 'ne' | 'sw' | 'se'; startMouseX: number; startMouseY: number; startArea: { x: number; y: number; width: number; height: number } } | null;

interface UseCanvasPointerEventsParams {
  // Boxes/arrows
  boxes: Box[];
  // Top-level-only boxes (subitems excluded — see Box.parentBoxId's own comment),
  // used only for read-only iteration (marquee hit-testing, snap targets) — never for
  // drag/setBoxes logic, which must stay on the full `boxes` above.
  canvasBoxes: Box[];
  arrows: Arrow[];
  setBoxes: Dispatch<SetStateAction<Box[]>>;
  selectedBoxes: number[];
  setSelectedBoxes: Dispatch<SetStateAction<number[]>>;
  setNextId: Dispatch<SetStateAction<number>>;
  setNextBoxNum: Dispatch<SetStateAction<number>>;
  setNextCalloutNum: Dispatch<SetStateAction<number>>;
  pushHistoryEntry: (newBoxes: Box[], newArrows: Arrow[]) => void;

  // Box drag
  dragging: number | null;
  setDragging: Dispatch<SetStateAction<number | null>>;
  offset: Point;
  setOffset: Dispatch<SetStateAction<Point>>;
  clickStartPos: (Point & { boxId?: number }) | null;
  setClickStartPos: Dispatch<SetStateAction<(Point & { boxId?: number }) | null>>;
  setSnapLines: Dispatch<SetStateAction<SnapLine[]>>;

  // Canvas pan/zoom
  canvasRef: RefObject<HTMLDivElement>;
  canvasContentRef: RefObject<HTMLDivElement>;
  zoom: number;
  setZoom: Dispatch<SetStateAction<number>>;
  panOffset: Point;
  setPanOffset: Dispatch<SetStateAction<Point>>;

  // Marquee selection
  selectionRect: SelectionRectState | null;
  setSelectionRect: Dispatch<SetStateAction<SelectionRectState | null>>;
  setMousePos: Dispatch<SetStateAction<Point>>;

  // Drag-to-create (draft box)
  draftBox: DraftBox | null;
  setDraftBox: Dispatch<SetStateAction<DraftBox | null>>;
  // Whether callout/comment boxes are currently shown (View settings) — a callout
  // placed while this is off is immediately filtered out of the render, which reads
  // as the comment silently vanishing. onPlaceCalloutHidden lets the parent warn
  // about that (owns the i18n string, this hook stays i18n-free) right before placing.
  showNotes: boolean;
  onPlaceCalloutHidden: () => void;

  // Text-edit / click-connect / context-menu interplay
  isEditingBoxTitle: number | null;
  setIsEditingBoxTitle: Dispatch<SetStateAction<number | null>>;
  setIsEditingText: Dispatch<SetStateAction<boolean>>;
  clickConnectMode: ClickConnectMode | null;
  setContextMenu: Dispatch<SetStateAction<{ x: number; y: number; type: 'box' | 'canvas'; boxId?: number } | null>>;
  setShowHistory: Dispatch<SetStateAction<boolean>>;
  handleBoxClickForConnect: (boxId: number) => void;

  // Areas: only the continue/finish half of draw/drag/resize (the start half lives
  // in FlowchartBuilder.tsx's canvas-level onMouseDown — see the file-level comment).
  setAreas: Dispatch<SetStateAction<Area[]>>;
  drawingArea: DrawingAreaState;
  setDrawingArea: Dispatch<SetStateAction<DrawingAreaState>>;
  setIsDrawingAreaMode: Dispatch<SetStateAction<boolean>>;
  setSelectedAreaId: Dispatch<SetStateAction<number | null>>;
  draggingArea: DraggingAreaState;
  setDraggingArea: Dispatch<SetStateAction<DraggingAreaState>>;
  resizingArea: ResizingAreaState;
  setResizingArea: Dispatch<SetStateAction<ResizingAreaState>>;
  nextAreaIdRef: MutableRefObject<number>;

  // Touch-specific, shared with FlowchartBuilder.tsx's own click-outside/canvas
  // mousedown handlers (both read isRecentTouchRef to avoid canceling edit mode
  // right after a double-tap) — created in the parent, passed through by reference.
  isRecentTouchRef: MutableRefObject<boolean>;
  touchTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
}

export function useCanvasPointerEvents(params: UseCanvasPointerEventsParams) {
  const {
    boxes, canvasBoxes, arrows, setBoxes, selectedBoxes, setSelectedBoxes,
    setNextId, setNextBoxNum, setNextCalloutNum, pushHistoryEntry,
    dragging, setDragging, offset, setOffset, clickStartPos, setClickStartPos, setSnapLines,
    canvasRef, canvasContentRef, zoom, setZoom, panOffset, setPanOffset,
    selectionRect, setSelectionRect, setMousePos,
    draftBox, setDraftBox, showNotes, onPlaceCalloutHidden,
    isEditingBoxTitle, setIsEditingBoxTitle, setIsEditingText, clickConnectMode, setContextMenu, setShowHistory, handleBoxClickForConnect,
    setAreas, drawingArea, setDrawingArea, setIsDrawingAreaMode, setSelectedAreaId,
    draggingArea, setDraggingArea, resizingArea, setResizingArea, nextAreaIdRef,
    isRecentTouchRef, touchTimerRef,
  } = params;

  const panOffsetRef = useRef(panOffset);
  const zoomRef = useRef(zoom);
  // Pan-in-progress state lives ONLY in this ref, not React state — starting a pan
  // used to call setIsPanning({...}), a state update on FlowchartBuilder that
  // re-renders its entire box/arrow tree just to flip the cursor to "grabbing".
  // React.memo bails out for every individual box/arrow, but on a large flowchart
  // (100+ boxes) just re-running the parent's render body and diffing that many
  // memoized children back-to-back was slow enough to cause a visible stutter at
  // the START of every single pan gesture (confirmed via user report: brief lag on
  // every pan-start, smooth once already dragging — exactly what you'd expect from
  // a one-time per-gesture render cost, not a leak). startPanning below sets this
  // ref and the cursor directly via the DOM instead, so starting a pan costs
  // nothing more than panOffsetRef already costs for continuing one.
  const isPanningRef = useRef<IsPanningState>(false);
  const startPanning = useCallback((startX: number, startY: number) => {
    isPanningRef.current = {
      startX, startY,
      initialPanX: panOffsetRef.current.x,
      initialPanY: panOffsetRef.current.y,
    };
    if (canvasContentRef.current) canvasContentRef.current.style.cursor = 'grabbing';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Applies zoom around a fixed anchor point (default: center of the visible viewport)
  // so that the point currently in view stays in place instead of shifting toward the canvas origin.
  //
  // Written directly to the ref + DOM instead of through setZoom/setPanOffset: this
  // runs on every wheel tick and every pinch touchmove frame, and a continuous zoom
  // gesture can fire many of those per second — unlike a pan-drag (which only
  // re-renders once, on release), going through React state here re-rendered the
  // whole box/arrow tree on every single one of those ticks, for the ENTIRE zoom
  // gesture, not just its start. zoomCommitTimerRef debounces the actual
  // setZoom/setPanOffset commit to ~150ms after the last call, so React state (and
  // anything reading it, like the topbar's zoom % label) catches up once the
  // gesture actually settles instead of on every tick — exactly mirroring why
  // panOffsetRef/isPanningRef exist for panning.
  const zoomCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const applyZoom = useCallback((computeZoom: (current: number) => number, anchor?: { x: number; y: number }) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const cx = anchor ? anchor.x : (rect ? rect.width / 2 : 0);
    const cy = anchor ? anchor.y : (rect ? rect.height / 2 : 0);
    const prevZoom = zoomRef.current;
    const prevPan = panOffsetRef.current;
    const newZoom = Math.max(0.2, Math.min(2, computeZoom(prevZoom)));
    const newPan = {
      x: cx - ((cx - prevPan.x) / prevZoom) * newZoom,
      y: cy - ((cy - prevPan.y) / prevZoom) * newZoom,
    };
    zoomRef.current = newZoom;
    panOffsetRef.current = newPan;
    if (canvasContentRef.current) {
      canvasContentRef.current.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${newZoom})`;
    }
    if (zoomCommitTimerRef.current) clearTimeout(zoomCommitTimerRef.current);
    zoomCommitTimerRef.current = setTimeout(() => {
      zoomCommitTimerRef.current = null;
      setZoom(zoomRef.current);
      setPanOffset(panOffsetRef.current);
    }, 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Flush a pending debounced zoom commit on unmount so its setState calls never
  // fire after the component (or the canvas view) is gone.
  useEffect(() => {
    return () => {
      if (zoomCommitTimerRef.current) clearTimeout(zoomCommitTimerRef.current);
    };
  }, []);

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // timer for long-press context menu (touch)
  const lastClickTimeRef = useRef<number>(0); // Track last click time for double-click detection (ref = no stale closure)
  const handleMoveRef = useRef<((e: MouseEvent | TouchEvent) => void) | null>(null);
  const handleUpRef   = useRef<((e: MouseEvent | TouchEvent) => void) | null>(null);
  const draftBoxRef = useRef(draftBox);
  const placeDraftBoxRef = useRef<(() => void) | null>(null);

  const handleDown = (e: React.MouseEvent | React.TouchEvent, box: Box): void => {
    // Double-tap timing: always track this before any early returns, so that taps on
    // readonly input elements are also counted correctly.
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    const isPotentialDoubleClick = timeSinceLastClick < 400;
    const isTouch = 'touches' in e;
    lastClickTimeRef.current = now;

    // Double-tap on touch → straight to edit mode, even if e.target is an INPUT
    if (isTouch && isPotentialDoubleClick && (box.type === 'regular' || box.type === 'callout')) {
      if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
      setDragging(null);
      setIsEditingBoxTitle(box.id);
      const boxEl = e.currentTarget as HTMLElement;
      requestAnimationFrame(() => {
        const textarea = boxEl.querySelector('textarea') as HTMLTextAreaElement | null;
        if (textarea) { textarea.focus(); textarea.select(); }
      });
      return;
    }

    if ((e.target as HTMLElement).tagName === 'INPUT' ||
        (e.target as HTMLElement).tagName === 'BUTTON' ||
        (e.target as HTMLElement).tagName === 'TEXTAREA') return;
    if ((e.target as HTMLElement).classList.contains('resize-handle')) return;
    setShowHistory(false);

    // Blur any focused input so cursor stops blinking
    if (document.activeElement instanceof HTMLElement &&
        (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
      document.activeElement.blur();
    }

    // preventDefault only for mouse (React's onTouchStart is passive and doesn't allow this)
    if (!isTouch && !isPotentialDoubleClick) {
      e.preventDefault();
    }

    const cx = (e as React.MouseEvent).clientX || ((e as React.TouchEvent).touches && (e as React.TouchEvent).touches[0].clientX);
    const cy = (e as React.MouseEvent).clientY || ((e as React.TouchEvent).touches && (e as React.TouchEvent).touches[0].clientY);

    // Store initial click position to detect clicks vs drags
    setClickStartPos({ x: cx, y: cy, boxId: box.id });

    // Exit edit mode when clicking on a different box (or anywhere else)
    if (isEditingBoxTitle !== null && isEditingBoxTitle !== box.id) {
      setIsEditingBoxTitle(null);
      setIsEditingText(false);
    }

    // If in Quick Connect mode, don't do normal selection/dragging
    if (clickConnectMode !== null) {
      return; // Let handleUp handle the click for connection
    }

    // Multi-select with Ctrl/Cmd/Shift key
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      if (selectedBoxes.includes(box.id)) {
        setSelectedBoxes(selectedBoxes.filter(id => id !== box.id));
      } else {
        setSelectedBoxes([...selectedBoxes, box.id]);
      }
      return; // Don't start dragging when selecting
    }

    // Don't start dragging immediately - wait for movement
    // This allows double-click to work properly
    // Dragging will start in handleMove when mouse actually moves

    // If this box is not in selection, select only this box
    if (!selectedBoxes.includes(box.id)) {
      setSelectedBoxes([box.id]);
    }

    setOffset({ x: cx - box.x * zoom - panOffset.x, y: cy - box.y * zoom - panOffset.y });

    // Long-press context menu for touch
    if (isTouch) {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        if (!selectedBoxes.includes(box.id)) setSelectedBoxes([box.id]);
        setContextMenu({ x: cx, y: cy, type: 'box', boxId: box.id });
      }, 500);
    }
  };

  /**
   * Handles mouse/touch move events (dragging boxes, panning canvas, selection)
   * @param e - Mouse or touch event
   */
  const handleMove = useCallback((e: MouseEvent | TouchEvent): void => {
    const cx = (e as MouseEvent).clientX || ((e as TouchEvent).touches && (e as TouchEvent).touches[0].clientX);
    const cy = (e as MouseEvent).clientY || ((e as TouchEvent).touches && (e as TouchEvent).touches[0].clientY);

    // Handle canvas panning — isPanningRef, not React state, see its own comment above.
    if (isPanningRef.current) {
      const pan = isPanningRef.current;
      // Self-heal: if the pan button was released outside the browser window
      // (Firefox then doesn't deliver a mouseup without explicit pointer capture),
      // isPanningRef would otherwise stay stuck forever until a refresh. As soon as
      // the cursor moves again while the right/middle button is no longer
      // pressed, we stop the panning after all.
      const buttons = (e as MouseEvent).buttons;
      if (typeof buttons === 'number' && (buttons & 0b0110) === 0) {
        isPanningRef.current = false;
        if (canvasContentRef.current) canvasContentRef.current.style.cursor = '';
        setPanOffset(panOffsetRef.current);
        return;
      }
      // Written directly to the ref + DOM instead of through setPanOffset here: this
      // branch runs on every mousemove while panning, and going through React state
      // re-renders the whole box/arrow tree on every single frame of the drag, which
      // is very noticeable on a large flowchart. handleUp() commits the final value
      // back to panOffset state once the drag ends, so nothing else needs to know a
      // pan is in progress until then. See canvasContentRef for the other half.
      const deltaX = cx - pan.startX;
      const deltaY = cy - pan.startY;
      const nextPan = {
        x: pan.initialPanX + deltaX,
        y: pan.initialPanY + deltaY,
      };
      panOffsetRef.current = nextPan;
      if (canvasContentRef.current) {
        canvasContentRef.current.style.transform = `translate(${nextPan.x}px, ${nextPan.y}px) scale(${zoomRef.current})`;
      }
      return;
    }

    // Resize area via corner handle
    if (resizingArea && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = (cx - rect.left - panOffset.x) / zoom;
      const mouseY = (cy - rect.top - panOffset.y) / zoom;
      const dx = mouseX - resizingArea.startMouseX;
      const dy = mouseY - resizingArea.startMouseY;
      const { x: sx, y: sy, width: sw, height: sh } = resizingArea.startArea;
      const MIN = 40;
      setAreas(prev => prev.map(a => {
        if (a.id !== resizingArea.id) return a;
        switch (resizingArea.handle) {
          case 'nw': { const nw = Math.max(MIN, sw - dx); const nh = Math.max(MIN, sh - dy); return { ...a, x: sx + sw - nw, y: sy + sh - nh, width: nw, height: nh }; }
          case 'ne': { const nw = Math.max(MIN, sw + dx); const nh = Math.max(MIN, sh - dy); return { ...a, y: sy + sh - nh, width: nw, height: nh }; }
          case 'sw': { const nw = Math.max(MIN, sw - dx); return { ...a, x: sx + sw - nw, width: nw, height: Math.max(MIN, sh + dy) }; }
          case 'se': return { ...a, width: Math.max(MIN, sw + dx), height: Math.max(MIN, sh + dy) };
          default: return a;
        }
      }));
      return;
    }

    // Drawing an area: update the preview rectangle
    if (drawingArea && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setDrawingArea(prev => prev ? {
        ...prev,
        currentX: (cx - rect.left - panOffset.x) / zoom,
        currentY: (cy - rect.top - panOffset.y) / zoom,
      } : null);
      return;
    }

    // Dragging an area: move the area
    if (draggingArea && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = (cx - rect.left - panOffset.x) / zoom;
      const mouseY = (cy - rect.top - panOffset.y) / zoom;
      setAreas(prev => prev.map(a => a.id === draggingArea.id
        ? { ...a, x: draggingArea.startAreaX + (mouseX - draggingArea.startMouseX), y: draggingArea.startAreaY + (mouseY - draggingArea.startMouseY) }
        : a
      ));
      return;
    }

    // Start dragging if mouse moved more than 3 pixels and we have clickStartPos
    // This allows double-click to work without interference
    if (!dragging && clickStartPos && clickStartPos.boxId && clickConnectMode === null) {
      const deltaX = Math.abs(cx - clickStartPos.x);
      const deltaY = Math.abs(cy - clickStartPos.y);

      if (deltaX > 3 || deltaY > 3) {
        // Actual movement detected - start dragging
        setDragging(clickStartPos.boxId);
        // Cancel the long-press timer when movement occurs
        if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
        // Exit edit mode when starting to drag
        setIsEditingBoxTitle(null);
      }
    }

    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = (cx - rect.left - panOffset.x) / zoom;
      const mouseY = (cy - rect.top - panOffset.y) / zoom;
      setMousePos({ x: mouseX, y: mouseY });

      // Update draft box position when dragging to create (with snap)
      if (draftBox) {
        const tentative = {
          x: mouseX - draftBox.width / 2,
          y: mouseY - draftBox.height / 2,
          width: draftBox.width,
          height: draftBox.height,
        };
        const { snapX, snapY, lines } = computeSnap(tentative, canvasBoxes, SNAP_THRESHOLD);
        setSnapLines(lines);
        setDraftBox({
          ...draftBox,
          x: snapX !== null ? snapX : tentative.x,
          y: snapY !== null ? snapY : tentative.y,
        });
      }

      // Update selection rectangle if active
      if (selectionRect) {
        setSelectionRect({
          ...selectionRect,
          currentX: mouseX,
          currentY: mouseY
        });

        // Calculate which boxes are in selection
        const minX = Math.min(selectionRect.startX, mouseX);
        const maxX = Math.max(selectionRect.startX, mouseX);
        const minY = Math.min(selectionRect.startY, mouseY);
        const maxY = Math.max(selectionRect.startY, mouseY);

        const boxesInSelection = canvasBoxes
          .filter(b => b.type !== 'startday') // Exclude start day boxes
          .filter(b => {
            const boxRight = b.x + b.width;
            const boxBottom = b.y + b.height;
            return b.x < maxX && boxRight > minX && b.y < maxY && boxBottom > minY;
          })
          .map(b => b.id);

        setSelectedBoxes(boxesInSelection);
      }
    }

    if (dragging) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const nx = (cx - rect.left - panOffset.x) / zoom - offset.x / zoom;
      const ny = (cy - rect.top - panOffset.y) / zoom - offset.y / zoom;

      // Calculate delta movement
      const draggedBox = boxes.find(b => b.id === dragging);
      if (draggedBox) {
        const deltaX = nx - draggedBox.x;
        const deltaY = ny - draggedBox.y;

        // Calculate new positions for all selected boxes
        let newBoxes = boxes.map(b => {
          if (selectedBoxes.includes(b.id)) {
            return {
              ...b,
              x: b.x + deltaX,
              y: b.y + deltaY
            };
          }
          return b;
        });

        // Snap: dragged box aligns to other boxes (edges, centers, equidistance)
        const updatedDraggedBox = newBoxes.find(b => b.id === dragging);
        // Exclude all selected boxes so they don't snap to each other
        const otherBoxes = canvasBoxes.filter(b => !selectedBoxes.includes(b.id));

        if (updatedDraggedBox && otherBoxes.length > 0) {
          const { snapX, snapY, lines } = computeSnap(updatedDraggedBox, otherBoxes, SNAP_THRESHOLD);
          setSnapLines(lines);

          if (snapX !== null || snapY !== null) {
            const dx = snapX !== null ? snapX - updatedDraggedBox.x : 0;
            const dy = snapY !== null ? snapY - updatedDraggedBox.y : 0;
            newBoxes = newBoxes.map(b =>
              selectedBoxes.includes(b.id) ? { ...b, x: b.x + dx, y: b.y + dy } : b
            );
          }
        } else {
          setSnapLines([]);
        }

        setBoxes(newBoxes);
      }
    }
  }, [dragging, zoom, offset, boxes, canvasBoxes, selectedBoxes, selectionRect, draftBox, panOffset, clickStartPos, clickConnectMode, drawingArea, draggingArea, resizingArea]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handles mouse/touch up events (finish drag, finalize selection, place draft box)
   * @param e - Mouse or touch event
   */
  const handleUp = useCallback((e: MouseEvent | TouchEvent): void => {
    // Finalize area drawing
    if (drawingArea) {
      const { startX, startY, currentX, currentY } = drawingArea;
      const x = Math.min(startX, currentX);
      const y = Math.min(startY, currentY);
      const w = Math.abs(currentX - startX);
      const h = Math.abs(currentY - startY);
      if (w > 20 && h > 20) {
        const newArea: Area = { id: nextAreaIdRef.current++, x, y, width: w, height: h, title: 'Top-level', color: '#6366f1' };
        setAreas(prev => [...prev, newArea]);
        setSelectedAreaId(newArea.id);
      }
      setDrawingArea(null);
      setIsDrawingAreaMode(false);
      return;
    }
    // Stop area drag / resize
    if (draggingArea) { setDraggingArea(null); return; }
    if (resizingArea) { setResizingArea(null); return; }

    // Cancel the long-press timer on release
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
    // Check if this was a click (not a drag) on a box for click-connect mode
    // ONLY if Quick Connect mode is actually active (not null)
    if (clickStartPos && clickConnectMode !== null) {
      const cx = (e as MouseEvent).clientX || ((e as TouchEvent).changedTouches && (e as TouchEvent).changedTouches[0]?.clientX) || clickStartPos.x;
      const cy = (e as MouseEvent).clientY || ((e as TouchEvent).changedTouches && (e as TouchEvent).changedTouches[0]?.clientY) || clickStartPos.y;

      const deltaX = Math.abs(cx - clickStartPos.x);
      const deltaY = Math.abs(cy - clickStartPos.y);

      // If movement was less than 5 pixels, treat as click
      if (deltaX < 5 && deltaY < 5 && clickStartPos.boxId) {
        handleBoxClickForConnect(clickStartPos.boxId);
      }
    }

    // If there was a selection rectangle but it was very small (just a click), deselect
    if (selectionRect) {
      const deltaX = Math.abs(selectionRect.currentX - selectionRect.startX);
      const deltaY = Math.abs(selectionRect.currentY - selectionRect.startY);

      // If movement was less than 5 pixels, treat as click (deselect)
      if (deltaX < 5 && deltaY < 5) {
        setSelectedBoxes([]);
      }
    }

    setDragging(null);
    setSelectionRect(null);
    isPanningRef.current = false;
    if (canvasContentRef.current) canvasContentRef.current.style.cursor = '';
    // Commits whatever handleMove wrote straight to panOffsetRef/the DOM during an
    // in-progress pan (see there for why) back into React state now that the drag
    // is over. A no-op setState when nothing was panning, since the ref already
    // matches state via the effect that mirrors panOffset into panOffsetRef.
    setPanOffset(panOffsetRef.current);
    setSnapLines([]); // Clear snap lines when drag ends
    setClickStartPos(null); // Clear click start position
  }, [selectionRect, clickStartPos, clickConnectMode, handleBoxClickForConnect, drawingArea, draggingArea, resizingArea]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [handleMove, handleUp]);

  // Sync refs so touch handlers always read up-to-date values
  useEffect(() => { panOffsetRef.current = panOffset; }, [panOffset]);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  // Sync handleMove/handleUp refs so the touch effect never needs to re-register
  handleMoveRef.current = handleMove;
  handleUpRef.current   = handleUp;
  draftBoxRef.current   = draftBox;
  placeDraftBoxRef.current = () => {
    if (!draftBoxRef.current) return;
    const db = draftBoxRef.current;
    draftBoxRef.current = null; // reset immediately so touch handlers no longer see it
    if (db.type === 'callout' && !showNotes) onPlaceCalloutHidden();
    const newBoxes = [...boxes, db as unknown as Box];
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
    setNextId(prev => prev + 1);
    if (db.type === 'regular') setNextBoxNum(prev => prev + 1);
    if (db.type === 'callout') setNextCalloutNum(prev => prev + 1);
    setDraftBox(null);
  };

  // ── Touch: everything in a single window handler ──────────────────────────
  // Pinch state is lazily initialized on the first two-finger touchmove,
  // so the order of touchstart events has no effect.
  useEffect(() => {
    // IMPORTANT: use canvasRef.current INSIDE the handlers (don't capture it once as `el`).
    // The canvas div can remount when the user opens and closes the ProjectList or Gantt view
    // (early-return paths). If we capture `el` once, it would point to the OLD div element after
    // a remount and el.contains(...) would always return false → pan/zoom broken.
    // Reading canvasRef.current inside each handler always gets us the current element.

    let touchOnBox = false;
    let touchOnCanvas = false;
    let prevPinchA: { x: number; y: number } | null = null;
    let prevPinchB: { x: number; y: number } | null = null;
    let canvasPanPrev: { x: number; y: number } | null = null;

    const onTouchStart = (e: TouchEvent) => {
      // Read the current canvas element at the time of the event
      const canvas = canvasRef.current;
      // Only prevent browser scroll/zoom for touches on the canvas itself (not on toolbar/sidebar).
      // Interactive elements within the canvas (buttons, inputs) are also excluded
      // so their click events keep working.
      const isOnCanvas = canvas ? canvas.contains(e.target as Node) : false;
      const isInteractive = !!(e.target as Element)?.closest('button, input, select, textarea, a, [role="button"]');
      if (isOnCanvas && !isInteractive) e.preventDefault();
      // Mark that a real touch occurred — prevents the synthetic mousedown after touch from canceling edit mode
      isRecentTouchRef.current = true;
      if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
      touchTimerRef.current = setTimeout(() => { isRecentTouchRef.current = false; }, 600);
      if (e.touches.length === 1) {
        touchOnCanvas = isOnCanvas;
        touchOnBox = !!(e.target as Element).closest('[data-box-el]');
        prevPinchA = null; prevPinchB = null; canvasPanPrev = null;
        if (!touchOnBox) {
          if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
          // Guarded (not unconditional) for the same reason FlowchartBuilder.tsx's
          // mouse onMouseDown now guards setContextMenu(null): calling a setter with
          // the value it already holds (the common case — nothing was being
          // dragged/click-tracked before this new touch) still re-renders
          // FlowchartBuilder's entire, very large function body once, even though
          // these setters are called from this hook rather than that file directly
          // — the state is owned there regardless of which file calls the setter.
          // Fires on every single-finger touch-pan start and (see the `>= 2` branch
          // below) every pinch-zoom start.
          if (clickStartPos !== null) setClickStartPos(null);
          if (dragging !== null) setDragging(null);
        }
      } else if (e.touches.length >= 2) {
        touchOnBox = false;
        touchOnCanvas = true; // always treat a pinch as a canvas gesture
        prevPinchA = null; prevPinchB = null; canvasPanPrev = null;
        if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
        if (clickStartPos !== null) setClickStartPos(null);
        if (dragging !== null) setDragging(null);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        e.preventDefault();
        const a = e.touches[0], b = e.touches[1];
        if (!prevPinchA || !prevPinchB) {
          prevPinchA = { x: a.clientX, y: a.clientY };
          prevPinchB = { x: b.clientX, y: b.clientY };
          return;
        }
        const prevDist = Math.hypot(prevPinchA.x - prevPinchB.x, prevPinchA.y - prevPinchB.y);
        const curDist  = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        const prevMidX = (prevPinchA.x + prevPinchB.x) / 2;
        const prevMidY = (prevPinchA.y + prevPinchB.y) / 2;
        const curMidX  = (a.clientX + b.clientX) / 2;
        const curMidY  = (a.clientY + b.clientY) / 2;
        const dx = curMidX - prevMidX;
        const dy = curMidY - prevMidY;
        const ratio = prevDist > 1 ? curDist / prevDist : 1;
        if (prevDist > 1) {
          const rect = canvasRef.current?.getBoundingClientRect();
          const anchor = rect ? { x: curMidX - rect.left, y: curMidY - rect.top } : undefined;
          applyZoom(prev => Math.max(0.2, Math.min(2, prev * ratio)), anchor);
        }
        // Written to the ref + DOM directly, same as every other continuous
        // pan/zoom update — this used to call setPanOffset(prev => ...) straight
        // through React state on every single pinch touchmove frame, re-rendering
        // the whole box/arrow tree for the entire gesture (the exact same class of
        // bug already fixed for mouse-wheel/pinch zoom and mouse panning). Reading
        // panOffsetRef.current here (rather than a stale local `prev`) picks up
        // whatever applyZoom just wrote above, so the zoom-anchor shift and this
        // two-finger pan shift compose correctly. onTouchEnd already calls
        // handleUp unconditionally, which commits this ref to panOffset state once
        // the gesture ends — no separate debounce needed here.
        if (Math.abs(dx) > 0 || Math.abs(dy) > 0) {
          const nextPan = { x: panOffsetRef.current.x + dx, y: panOffsetRef.current.y + dy };
          panOffsetRef.current = nextPan;
          if (canvasContentRef.current) {
            canvasContentRef.current.style.transform = `translate(${nextPan.x}px, ${nextPan.y}px) scale(${zoomRef.current})`;
          }
        }
        prevPinchA = { x: a.clientX, y: a.clientY };
        prevPinchB = { x: b.clientX, y: b.clientY };
        return;
      }

      // 1 finger — only process if the touch started on the canvas
      prevPinchA = null; prevPinchB = null;
      if (!touchOnCanvas) return;

      // If a draft box is active, update its position via handleMove instead of panning
      if (draftBoxRef.current) {
        handleMoveRef.current?.(e);
        return;
      }

      if (!touchOnBox) {
        e.preventDefault();
        const t = e.touches[0];
        if (!canvasPanPrev) {
          canvasPanPrev = { x: t.clientX, y: t.clientY };
          return;
        }
        const dx = t.clientX - canvasPanPrev.x;
        const dy = t.clientY - canvasPanPrev.y;
        // Same reasoning as the mouse-drag pan in handleMove: write straight to the
        // ref/DOM per touchmove frame, let handleUp (called from onTouchEnd) commit
        // the final value to panOffset state once.
        const nextPan = { x: panOffsetRef.current.x + dx, y: panOffsetRef.current.y + dy };
        panOffsetRef.current = nextPan;
        if (canvasContentRef.current) {
          canvasContentRef.current.style.transform = `translate(${nextPan.x}px, ${nextPan.y}px) scale(${zoomRef.current})`;
        }
        canvasPanPrev = { x: t.clientX, y: t.clientY };
        return;
      }

      handleMoveRef.current?.(e);
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) { prevPinchA = null; prevPinchB = null; }
      if (e.touches.length === 0) {
        // Place the draft box on release over the canvas
        if (touchOnCanvas && draftBoxRef.current) {
          placeDraftBoxRef.current?.();
          canvasPanPrev = null; touchOnBox = false; touchOnCanvas = false;
          return;
        }
        canvasPanPrev = null; touchOnBox = false; touchOnCanvas = false;
      }
      // Always call this (even with touches=1, e.g. releasing the first finger during a pinch)
      handleUpRef.current?.(e);
    };

    const onWheel = (e: WheelEvent) => {
      // Check whether the wheel event occurred on the current canvas element
      const canvas = canvasRef.current;
      if (!canvas || !canvas.contains(e.target as Node)) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const rect = canvas.getBoundingClientRect();
      applyZoom(prev => Math.max(0.2, Math.min(2, prev + delta)), { x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    window.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd as EventListener);
    window.addEventListener('touchcancel', onTouchEnd as EventListener);
    // Wheel also on window (not on el) so it keeps working after a canvas remount
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd as EventListener);
      window.removeEventListener('touchcancel', onTouchEnd as EventListener);
      window.removeEventListener('wheel', onWheel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyZoom]); // applyZoom is stable (useCallback with empty deps); the setters/refs above are
  // stable too (useState/useRef results passed through as params) — refs provide up-to-date values

  // isPanningRef itself (not just a boolean getter) so callers can cheaply check
  // "is a pan in progress right now" from their own event handlers — see
  // FlowchartBuilder.tsx's onMouseMove (broadcastCursor) and useBoxActions.ts's
  // mouseEnter/mouseLeave for the two places this matters.
  return { handleDown, applyZoom, startPanning, isPanningRef };
}
